/**
 * Site Management Types for Choice Energy Tendering System
 */

export type RateType = 'fixed' | 'variable' | 'time-of-use';

// ============================================================================
// Current Contract Rates — structured rate types for all 4 charge categories
// ============================================================================

export type CurrentContractEnergyRates = {
  peak_rate: number;         // c/kWh
  offpeak_rate: number;      // c/kWh
  shoulder_rate: number;     // c/kWh
  daily_supply_rate: number; // c/day
};

export type CurrentContractNetworkRates = {
  winter_peak_rate: number;    // c/kWh (applied to annual_kwh_peak)
  winter_offpeak_rate: number; // c/kWh (applied to annual_kwh_offpeak)
  capacity_rate: number;       // c/kW/year (applied to peak_demand_kw)
  supply_charge_rate: number;  // c/site/year (flat annual)
};

export type CurrentContractEnvironmentalRates = {
  veec_rate: number;              // c/kWh
  lrec_rate: number;              // c/kWh
  srec_rate: number;              // c/kWh
  esc_rate: number;               // c/kWh (NSW Energy Savings Certificates)
  reps_rate: number;              // c/kWh (SA Retailer Energy Productivity Scheme)
  eeis_rate: number;              // c/kWh (ACT Energy Efficiency Improvement Scheme)
  veec_power_percentage: number;  // % of total_kwh to apply veec to (0 = use full total)
  lrec_power_percentage: number;  // % of total_kwh
  srec_power_percentage: number;  // % of total_kwh
  esc_power_percentage: number;   // % of total_kwh (NSW only)
  reps_power_percentage: number;  // % of total_kwh (SA only)
  eeis_power_percentage: number;  // % of total_kwh (ACT only)
};

export type CurrentContractOtherRates = {
  meter_supply_rate: number;      // $/meter/DAY  → engine multiplies × 365 to get annual cost
  service_charge_rate: number;    // $/month      → engine multiplies × 12 to get annual cost
  aemo_participant_rate: number;  // c/kW/year    → engine does peak_kw × rate × 0.01
  aemo_ancillary_rate: number;    // c/kW/year    → engine does peak_kw × rate × 0.01
};

export type CurrentContractRates = {
  energy: CurrentContractEnergyRates;
  network: CurrentContractNetworkRates;
  environmental: CurrentContractEnvironmentalRates;
  other: CurrentContractOtherRates;
};

export const createEmptyCurrentContractEnergyRates = (): CurrentContractEnergyRates => ({
  peak_rate: 0,
  offpeak_rate: 0,
  shoulder_rate: 0,
  daily_supply_rate: 0,
});

export const createEmptyCurrentContractNetworkRates = (): CurrentContractNetworkRates => ({
  winter_peak_rate: 0,
  winter_offpeak_rate: 0,
  capacity_rate: 0,
  supply_charge_rate: 0,
});

export const createEmptyCurrentContractEnvironmentalRates = (): CurrentContractEnvironmentalRates => ({
  veec_rate: 0,
  lrec_rate: 0,
  srec_rate: 0,
  esc_rate: 0,
  reps_rate: 0,
  eeis_rate: 0,
  veec_power_percentage: 0,
  lrec_power_percentage: 0,
  srec_power_percentage: 0,
  esc_power_percentage: 0,
  reps_power_percentage: 0,
  eeis_power_percentage: 0,
});

export const createEmptyCurrentContractOtherRates = (): CurrentContractOtherRates => ({
  meter_supply_rate: 0,
  service_charge_rate: 0,
  aemo_participant_rate: 0,
  aemo_ancillary_rate: 0,
});

export const createEmptyCurrentContractRates = (): CurrentContractRates => ({
  energy: createEmptyCurrentContractEnergyRates(),
  network: createEmptyCurrentContractNetworkRates(),
  environmental: createEmptyCurrentContractEnvironmentalRates(),
  other: createEmptyCurrentContractOtherRates(),
});

export interface SiteAddress {
  street: string;
  city: string;
  state: string;
  postcode: string;
  country: string;
}

export interface EnergyDetails {
  offerExpiry: string;
  rateType: RateType;
  peakRate: number;
  offpeakRate: number;
  shoulderRate: number;
  cPerKwh: number;
  nonEnergyCharges: number;
}

export interface Site {
  id: string;
  siteId?: string; // Global Site ID (FK to site table)
  orgId?: string;
  clientId?: string;
  name: string;
  nmi: string;
  abn?: string;
  acn?: string;
  dueAt?: Date;
  currentContractExpiry?: Date;
  currentMeterProvider?: string;
  currentRetailer?: string;
  dmaProvider?: string;
  retailerComms?: string;
  createDma: boolean;
  requestMeterData: boolean;
  requestBills: boolean;
  acquireCed: boolean;
  greenPower: boolean;  // Green Power option
  annualKwhPeak?: number;
  annualKwhOffpeak?: number;
  annualKwhShoulder?: number;
  annualKwhTotal?: number;  // Total usage (auto-calculated sum of peak, off-peak, shoulder)
  dailyCharges?: number;  // Daily supply charges (c/day)
  nonEnergyCharges?: number;  // Non-energy charges ($)
  peakDemandKw?: number;  // Peak demand in kilowatts - for demand charge calculations
  peakDemandKva?: number;  // Peak demand in kilovolt-amperes - alternate unit
  currentContractRates?: CurrentContractRates;  // Structured current contract rates for all 4 categories
  documentFilePaths?: string[];  // Array of file paths to site documents in Supabase storage
  retailerNotes?: string;  // Notes for retailers regarding this site
  address: SiteAddress;
  energyDetails: EnergyDetails;
  extn: Record<string, any>;
  createdAt: Date;
  // UI state properties
  isExpanded: boolean;
  isNew: boolean;
  isValid: boolean;
}

export interface SiteValidation {
  isValid: boolean;
  errors: {
    name?: string;
    nmi?: string;
    abn?: string;
    acn?: string;
    currentContractExpiry?: string;
    currentMeterProvider?: string;
    currentRetailer?: string;
    annualKwhPeak?: string;
    annualKwhOffpeak?: string;
    annualKwhShoulder?: string;
    address?: {
      street?: string;
      city?: string;
      state?: string;
      postcode?: string;
    };
    energyDetails?: {
      offerExpiry?: string;
      peakRate?: string;
      offpeakRate?: string;
      shoulderRate?: string;
      cPerKwh?: string;
      nonEnergyCharges?: string;
    };
    environmental?: {
      veec_rate?: string;
      lrec_rate?: string;
      srec_rate?: string;
    };
    energyRates?: {
      peak_rate?: string;
      offpeak_rate?: string;
    };
  };
}

// Australian states for validation
export const AUSTRALIAN_STATES = [
  { value: 'NSW', label: 'New South Wales' },
  { value: 'VIC', label: 'Victoria' },
  { value: 'QLD', label: 'Queensland' },
  { value: 'WA', label: 'Western Australia' },
  { value: 'SA', label: 'South Australia' },
  { value: 'TAS', label: 'Tasmania' },
  { value: 'ACT', label: 'Australian Capital Territory' },
  { value: 'NT', label: 'Northern Territory' },
];

// Rate types for dropdown
export const RATE_TYPES = [
  { value: 'fixed', label: 'Fixed Rate' },
  { value: 'variable', label: 'Variable Rate' },
  { value: 'time-of-use', label: 'Time of Use' },
];

// Common Australian energy retailers
export const ENERGY_RETAILERS = [
  'AGL Energy',
  'Origin Energy',
  'EnergyAustralia',
  'Red Energy',
  'Alinta Energy',
  'Simply Energy',
  'Momentum Energy',
  'Powershop',
  'Lumo Energy',
  'GloBird Energy',
  'Other',
];

// Common meter providers
export const METER_PROVIDERS = [
  'Ausgrid',
  'Endeavour Energy',
  'Essential Energy',
  'Powercor',
  'CitiPower',
  'United Energy',
  'AusNet Services',
  'Jemena',
  'SA Power Networks',
  'Western Power',
  'TasNetworks',
  'Power and Water Corporation',
  'Powermetric',
  'Mondo',
  'Intellihub',
  'Metropolis',
  'Acumen',
  'Other',
];

// DMA (Data Management Agent) providers
export const DMA_PROVIDERS = [
  'Mondo',
  'Powermetric',
];

// Retailer Commission values
export const RETAILER_COMMS = [
  '0%',
  '0.1c',
  '0.2c',
  '0.4c',
  '1.0%',
  '1.5%',
  '2.0%',
  '2.5%',
  '3.0%',
  '3.5%',
];

// Default site template
export const createDefaultSite = (): Site => ({
  id: `site_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
  name: '',
  nmi: '',
  abn: '',
  acn: '',
  dueAt: undefined,
  currentContractExpiry: undefined,
  currentMeterProvider: '',
  currentRetailer: '',
  dmaProvider: '',
  retailerComms: '',
  createDma: false,
  requestMeterData: false,
  requestBills: false,
  acquireCed: false,
  greenPower: false,  // Green Power option
  annualKwhPeak: undefined,
  annualKwhOffpeak: undefined,
  annualKwhShoulder: undefined,
  annualKwhTotal: undefined,  // Auto-calculated total
  dailyCharges: 0,  // Daily supply charges (c/day)
  nonEnergyCharges: 0,  // Non-energy charges ($)
  peakDemandKw: undefined,  // Peak demand in kW
  peakDemandKva: undefined,  // Peak demand in kVA
  currentContractRates: createEmptyCurrentContractRates(),
  documentFilePaths: [],  // NEW: Array for storing document file paths
  retailerNotes: '',  // NEW: Retailer notes field
  address: {
    street: '',
    city: '',
    state: '',
    postcode: '',
    country: 'Australia',
  },
  energyDetails: {
    offerExpiry: '',
    rateType: 'fixed',
    peakRate: 0,
    offpeakRate: 0,
    shoulderRate: 0,
    cPerKwh: 0,
    nonEnergyCharges: 0,
  },
  extn: {},
  createdAt: new Date(),
  isExpanded: true,
  isNew: true,
  isValid: false,
});

// Site validation utilities - ENABLED
export const validateSite = (site: Site): SiteValidation => {
  const errors: SiteValidation['errors'] = {};
  let isValid = true;

  // Mandatory field: Site name
  // name/nmi are optional-chained because tender_site.name and tender_site.nmi are both
  // nullable in the DB (migration 049 documents "no NMI yet, mid-entry" as a valid state)
  // and this runs during render — a NULL would otherwise throw and break the whole step.
  if (!site.name?.trim()) {
    errors.name = 'Site name is required';
    isValid = false;
  }

  // Mandatory field: NMI
  if (!site.nmi?.trim()) {
    errors.nmi = 'NMI is required';
    isValid = false;
  } else if (!/^[A-Z0-9]{10,11}$/i.test(site.nmi.replace(/\s/g, ''))) {
    errors.nmi = 'NMI must be 10-11 alphanumeric characters';
    isValid = false;
  }

  // Optional: ABN validation (only if provided)
  if (site.abn && site.abn.trim()) {
    const cleanABN = site.abn.replace(/\s/g, '');
    if (!/^\d{11}$/.test(cleanABN)) {
      errors.abn = 'ABN must be 11 digits';
      isValid = false;
    }
  }

  // Optional: ACN validation — accept 9 OR 11 digits (some entities use 11-digit format)
  if (site.acn && site.acn.trim()) {
    const cleanACN = site.acn.replace(/\s/g, '');
    if (!/^\d{9}$/.test(cleanACN) && !/^\d{11}$/.test(cleanACN)) {
      errors.acn = 'ACN must be 9 or 11 digits';
      isValid = false;
    }
  }

  // Current contract expiry — OPTIONAL, deliberately.
  //
  // The engine derives the contract's start date from it (start = expiry + 1) and every term
  // weight is measured from that, so a blank one costs accuracy: computeYear1DayFraction falls
  // back to a flat 364/365, which is only right for a contract starting on the 1st of a month.
  //
  // It is still not required, because a site that has never had a contract has no expiry to
  // give. Blocking those would make the step unusable for new connections. The contract START
  // is what the engine actually needs; expiry is only one way to reach it.

  // Mandatory: Annual usage (peak and off-peak required)
  if (!site.annualKwhPeak || site.annualKwhPeak <= 0) {
    errors.annualKwhPeak = 'Peak usage is required';
    isValid = false;
  }
  if (!site.annualKwhOffpeak || site.annualKwhOffpeak <= 0) {
    errors.annualKwhOffpeak = 'Off-peak usage is required';
    isValid = false;
  }
  if (site.annualKwhShoulder !== undefined && site.annualKwhShoulder < 0) {
    errors.annualKwhShoulder = 'Annual shoulder usage cannot be negative';
    isValid = false;
  }

  // Optional: Current contract energy rates (only validate if any rate has been entered)
  const energyRateErrors: SiteValidation['errors']['energyRates'] = {};
  const peakRate = site.currentContractRates?.energy?.peak_rate;
  const offpeakRate = site.currentContractRates?.energy?.offpeak_rate;
  const hasAnyCurrentRateData =
    (peakRate && peakRate > 0) ||
    (offpeakRate && offpeakRate > 0) ||
    ((site.currentContractRates?.energy?.shoulder_rate ?? 0) > 0);
  if (hasAnyCurrentRateData) {
    if (!peakRate || peakRate <= 0) {
      energyRateErrors.peak_rate = 'Peak rate is required when entering current contract rates';
      isValid = false;
    }
    if (!offpeakRate || offpeakRate <= 0) {
      energyRateErrors.offpeak_rate = 'Off-peak rate is required when entering current contract rates';
      isValid = false;
    }
  }
  if (Object.keys(energyRateErrors).length > 0) {
    errors.energyRates = energyRateErrors;
  }

  // Mandatory: Street address only (city, state, postcode are optional)
  const addressErrors: SiteValidation['errors']['address'] = {};
  const address = site.address || { street: '', city: '', state: '', postcode: '', country: '' };
  if (!address.street?.trim()) {
    addressErrors.street = 'Street address is required';
    isValid = false;
  }
  if (Object.keys(addressErrors).length > 0) {
    errors.address = addressErrors;
  }

  // Optional: Environmental charge rates (LREC, SREC, VEEC not required for new sites)

  // Optional: Energy details validation (not mandatory but validated if provided)
  const energyErrors: SiteValidation['errors']['energyDetails'] = {};

  // Only validate energy details if any energy field is filled
  const energyDetails = site.energyDetails || { offerExpiry: '', peakRate: 0, cPerKwh: 0 };
  const hasEnergyData = energyDetails.offerExpiry ||
    energyDetails.peakRate > 0 ||
    energyDetails.cPerKwh > 0;

  if (hasEnergyData) {
    if (!energyDetails.offerExpiry) {
      energyErrors.offerExpiry = 'Offer expiry date is required when energy details are provided';
      isValid = false;
    }
    if (energyDetails.peakRate <= 0) {
      energyErrors.peakRate = 'Peak rate must be greater than 0';
      isValid = false;
    }
    if (energyDetails.cPerKwh < 0) {
      energyErrors.cPerKwh = 'Daily charge cannot be negative';
      isValid = false;
    }
  }

  if (Object.keys(energyErrors).length > 0) {
    errors.energyDetails = energyErrors;
  }

  return { isValid, errors };
};

// NMI formatting utility - accepts alphanumeric characters
export const formatNMI = (nmi: string): string => {
  // Remove any characters that are not alphanumeric (letters and numbers)
  const cleaned = nmi.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
  if (cleaned.length <= 10) {
    return cleaned;
  }
  // Format as XXXXXXXXXX or XXXXXXXXXXX (max 11 characters)
  return cleaned.slice(0, 11);
};

// Postcode validation by state
export const validatePostcodeForState = (postcode: string, state: string): boolean => {
  const postcodeRanges: Record<string, [number, number][]> = {
    NSW: [[1000, 1999], [2000, 2599], [2619, 2899], [2921, 2999]],
    ACT: [[200, 299], [2600, 2618], [2900, 2920]],
    VIC: [[3000, 3999], [8000, 8999]],
    QLD: [[4000, 4999], [9000, 9999]],
    SA: [[5000, 5999]],
    WA: [[6000, 6797], [6800, 6999]],
    TAS: [[7000, 7999]],
    NT: [[800, 999]],
  };

  const code = parseInt(postcode);
  const ranges = postcodeRanges[state];

  if (!ranges) return true; // Unknown state, allow any postcode

  return ranges.some(([min, max]) => code >= min && code <= max);
};
